import { Padding, SVG_NS, attachHoverListeners, observeResize } from './shared';

/**
 * Configuration for a chart instance.
 */
export interface LineChartConfig {
  /** Array of data series. Each inner array is one series of numeric values. */
  data: number[][];
  /** X-axis labels. Must match the length of each data series. */
  labels: string[];
  /** X-axis title. */
  xLabel?: string;
  /** Y-axis title. */
  yLabel?: string;
  /** Colors for each series. Defaults to Thruscan brand palette. */
  colors?: string[];
  /** Chart width in pixels. Defaults to container's clientWidth or 600. */
  width?: number;
  /** Chart height in pixels. Defaults to 350. */
  height?: number;
  /** Catmull-Rom spline smoothing. Default false. */
  smooth?: boolean;
  /** Semi-transparent fill under each series. Default false. */
  areaFill?: boolean;
  /** Area fill opacity. Default 0.1. */
  areaOpacity?: number;
  /** Show X/Y tick labels. Default true. */
  showTicks?: boolean;
  /** Show grid lines. Default true. */
  showGrid?: boolean;
  /** Dashed grid lines. Default false. */
  gridDashed?: boolean;
  /** Series stroke width. Default 2.5. */
  strokeWidth?: number;
  /** Enable hover tooltip + indicator. Default true. */
  hover?: boolean;
  /** Chart padding {top, right, bottom, left}. Default {top:20, right:30, bottom:50, left:60}. */
  padding?: Partial<Padding>;
}


/**
 * Creates a high-performance SVG+HTML hybrid line chart inside the specified container.
 */
export function NanoLineChart(containerId: string, config: LineChartConfig): void {
  var data = config.data;
  var labels = config.labels;
  var xLabel = config.xLabel || '';
  var yLabel = config.yLabel || '';
  var colors = config.colors || ['#215CAF', '#007894', '#00C9A7', '#DE4437', '#F69C3D'];
  var dp = config.padding || {};
  var padding: Padding = { top: dp.top || 0, right: dp.right || 0, bottom: dp.bottom || 0, left: dp.left || 0 };

  var smooth = config.smooth === true;
  var areaFill = config.areaFill === true;
  var areaOpacity = config.areaOpacity !== undefined ? config.areaOpacity : 0.1;
  var showTicks = config.showTicks !== false;
  var showGrid = config.showGrid !== false;
  var gridDashed = config.gridDashed === true;
  var strokeWidth = config.strokeWidth !== undefined ? config.strokeWidth : 2.5;
  var hoverEnabled = config.hover !== false;

  var container = document.getElementById(containerId)!;
  if (!container || !data.length || !data[0].length) return;
  var chartWidth = config.width || container.clientWidth || 600;
  var chartHeight = config.height || 350;
  var plotWidth = chartWidth - padding.left - padding.right;
  var plotHeight = chartHeight - padding.top - padding.bottom;
  var plotBottom = padding.top + plotHeight;

  // ── Phase 1: Pure math (zero DOM) ──
  var seriesIndex: number, pointIndex: number, minValue: number, maxValue: number;
  minValue = maxValue = data[0][0];
  for (seriesIndex = 0; seriesIndex < data.length; seriesIndex++)
    for (pointIndex = 0; pointIndex < data[seriesIndex].length; pointIndex++) {
      if (data[seriesIndex][pointIndex] < minValue) minValue = data[seriesIndex][pointIndex];
      if (data[seriesIndex][pointIndex] > maxValue) maxValue = data[seriesIndex][pointIndex];
    }
  var range = maxValue - minValue || 1;
  minValue -= range * 0.05;
  maxValue += range * 0.05;
  range = maxValue - minValue;

  var pointCount = labels.length;
  var stepX = pointCount > 1 ? plotWidth / (pointCount - 1) : 0;
  var tickCount = 6;
  var tickStep = range / (tickCount - 1);

  // Int16Array — half memory of Float32Array, perfect for integer pixel coords
  var totalCoords = data.length * pointCount;
  var coordsX = new Int16Array(totalCoords);
  var coordsY = new Int16Array(totalCoords);
  for (seriesIndex = 0; seriesIndex < data.length; seriesIndex++)
    for (pointIndex = 0; pointIndex < pointCount; pointIndex++) {
      var coordIndex = seriesIndex * pointCount + pointIndex;
      coordsX[coordIndex] = (padding.left + pointIndex * stepX) | 0;
      coordsY[coordIndex] = (padding.top + plotHeight - ((data[seriesIndex][pointIndex] - minValue) / range) * plotHeight) | 0;
    }

  // Grid Y positions + labels
  var gridYPositions = new Int16Array(tickCount);
  var gridLabels = new Array(tickCount);
  var tickIndex: number;
  for (tickIndex = 0; tickIndex < tickCount; tickIndex++) {
    var tickValue = minValue + tickIndex * tickStep;
    gridYPositions[tickIndex] = (padding.top + plotHeight - ((tickValue - minValue) / range) * plotHeight) | 0;
    gridLabels[tickIndex] = tickValue.toFixed(1);
  }

  // ── Phase 2: Build SVG string ──
  var svgMarkup = '<svg xmlns="http://www.w3.org/2000/svg" width="' + chartWidth + '" height="' + chartHeight + '" viewBox="0 0 ' + chartWidth + ' ' + chartHeight + '" class="chart">';

  // Grid path
  if (showGrid) {
    var gridPath = '';
    for (tickIndex = 0; tickIndex < tickCount; tickIndex++)
      gridPath += 'M' + padding.left + ',' + gridYPositions[tickIndex] + 'H' + (padding.left + plotWidth);
    svgMarkup += '<path d="' + gridPath + '" class="chart-grid"' + (gridDashed ? ' stroke-dasharray="3 4"' : '') + '/>';
  }

  // Series paths
  for (seriesIndex = 0; seriesIndex < data.length; seriesIndex++) {
    var ci = seriesIndex * pointCount;
    var pathData = 'M' + coordsX[ci] + ',' + coordsY[ci];

    if (smooth) {
      // Catmull-Rom → Cubic Bezier, directly from Int16Array coords
      for (pointIndex = 0; pointIndex < pointCount - 1; pointIndex++) {
        var prevI = pointIndex ? pointIndex - 1 : 0;
        var next2I = pointIndex + 2 < pointCount ? pointIndex + 2 : pointCount - 1;
        var curX = coordsX[ci + pointIndex], curY = coordsY[ci + pointIndex];
        var nextX = coordsX[ci + pointIndex + 1], nextY = coordsY[ci + pointIndex + 1];
        var prevX = coordsX[ci + prevI], prevY = coordsY[ci + prevI];
        var next2X = coordsX[ci + next2I], next2Y = coordsY[ci + next2I];
        pathData += ' C' + ((curX + (nextX - prevX) / 6) | 0) + ',' + ((curY + (nextY - prevY) / 6) | 0)
          + ' ' + ((nextX - (next2X - curX) / 6) | 0) + ',' + ((nextY - (next2Y - curY) / 6) | 0)
          + ' ' + nextX + ',' + nextY;
      }
    } else {
      for (pointIndex = 1; pointIndex < pointCount; pointIndex++)
        pathData += 'L' + coordsX[ci + pointIndex] + ',' + coordsY[ci + pointIndex];
    }

    // Area fill path (before series line so line draws on top)
    if (areaFill) {
      var firstX = coordsX[ci], lastX = coordsX[ci + pointCount - 1];
      svgMarkup += '<path d="' + pathData + ' L' + lastX + ',' + plotBottom + ' L' + firstX + ',' + plotBottom + ' Z"'
        + ' fill="' + colors[seriesIndex % colors.length] + '" fill-opacity="' + areaOpacity + '" stroke="none" pointer-events="none"/>';
    }

    svgMarkup += '<path d="' + pathData + '" class="chart-series" stroke="' + colors[seriesIndex % colors.length] + '"'
      + (strokeWidth !== 2.5 ? ' stroke-width="' + strokeWidth + '"' : '') + '/>';
  }

  svgMarkup += '</svg>';

  // ── Phase 3: Build HTML label overlay ──
  var labelMarkup = '<div class="chart-labels">';

  if (showTicks) {
    // Y tick labels
    for (tickIndex = 0; tickIndex < tickCount; tickIndex++)
      labelMarkup += '<span class="chart-tick-y" style="left:' + (padding.left - 4) + 'px;top:' + (gridYPositions[tickIndex] - 1) + 'px">' + gridLabels[tickIndex] + '</span>';

    // X tick labels
    for (pointIndex = 0; pointIndex < pointCount; pointIndex++)
      labelMarkup += '<span class="chart-tick-x" style="left:' + coordsX[pointIndex] + 'px;top:' + (padding.top + plotHeight + 6) + 'px">' + labels[pointIndex] + '</span>';
  }

  // Axis titles
  if (xLabel)
    labelMarkup += '<span class="chart-title" style="left:' + (padding.left + (plotWidth >> 1)) + 'px;top:' + (chartHeight - 10) + 'px">' + xLabel + '</span>';
  if (yLabel)
    labelMarkup += '<span class="chart-title" style="left:4px;top:' + (padding.top - 14) + 'px;transform:none;text-align:left">' + yLabel + '</span>';

  labelMarkup += '</div>';

  // ── Phase 4: Single innerHTML write (SVG + HTML labels) ──
  container.style.cssText = 'position:relative;width:' + chartWidth + 'px;height:' + chartHeight + 'px';
  container.innerHTML = svgMarkup + labelMarkup;

  // ── Phase 5: Auto-resize ──
  if (!config.width) {
    observeResize(container, function () { NanoLineChart(containerId, config); });
  }

  // ── Phase 6: Lazy hover ──
  if (!hoverEnabled) return;

  var svg = container.firstChild as SVGSVGElement;
  var indicator: SVGCircleElement | null = null;
  var tooltip: HTMLDivElement | null = null;

  function ensureHoverElements() {
    if (!indicator) {
      indicator = document.createElementNS(SVG_NS, 'circle') as SVGCircleElement;
      indicator.setAttribute('r', '5');
      indicator.setAttribute('class', 'chart-indicator');
      indicator.style.display = 'none';
      svg.appendChild(indicator);
    }
    if (!tooltip) {
      tooltip = document.createElement('div') as HTMLDivElement;
      tooltip.className = 'chart-tooltip';
      container.appendChild(tooltip);
    }
  }

  attachHoverListeners(container, svg,
    function (mouseX, mouseY) {
      if (mouseX < padding.left || mouseX > padding.left + plotWidth || mouseY < padding.top || mouseY > padding.top + plotHeight) {
        if (indicator) indicator.style.display = 'none';
        if (tooltip) tooltip.style.opacity = '0';
        return;
      }

      ensureHoverElements();

      var relativeX = mouseX - padding.left;
      if (relativeX < 0) relativeX = 0;
      if (relativeX > plotWidth) relativeX = plotWidth;
      var nearestIndex = (relativeX / stepX + 0.5) | 0;
      if (nearestIndex >= pointCount) nearestIndex = pointCount - 1;

      var bestSeriesIndex = 0;
      var bestDistance = Math.abs(coordsY[nearestIndex] - mouseY);
      for (var searchIndex = 1; searchIndex < data.length; searchIndex++) {
        var distance = Math.abs(coordsY[searchIndex * pointCount + nearestIndex] - mouseY);
        if (distance < bestDistance) { bestDistance = distance; bestSeriesIndex = searchIndex; }
      }

      var pointX = coordsX[bestSeriesIndex * pointCount + nearestIndex];
      var pointY = coordsY[bestSeriesIndex * pointCount + nearestIndex];

      indicator!.setAttribute('transform', 'translate(' + pointX + ',' + pointY + ')');
      indicator!.setAttribute('fill', colors[bestSeriesIndex % colors.length]);
      indicator!.style.display = '';

      tooltip!.innerHTML = '<b>Series ' + (bestSeriesIndex + 1) + '</b><br>' +
        xLabel + ': ' + labels[nearestIndex] + '<br>' + yLabel + ': ' + data[bestSeriesIndex][nearestIndex];
      tooltip!.style.transform = 'translate(' + pointX + 'px,' + (pointY - 40) + 'px) translate(-50%,-100%)';
      tooltip!.style.opacity = '1';
    },
    function () {
      if (indicator) indicator.style.display = 'none';
      if (tooltip) tooltip.style.opacity = '0';
    }
  );
}

